import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLenis } from "@/hooks/useLenis";
import { useSEO } from "@/components/site/SEO";
import { SITE } from "@/config/site";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { FloatingCTA } from "@/components/site/FloatingCTA";
import { Reveal, Stagger, StaggerItem } from "@/components/site/Reveal";
import { RESIDENTIAL_CARDS, COMMERCIAL_CARDS } from "@/data/services";

const Grid = ({ cards }) => (
  <Stagger className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
    {cards.map((c) => (
      <StaggerItem key={c.name + c.href}>
        <div className="group flex h-full flex-col rounded-3xl border border-slate-200/70 bg-white p-7 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all hover:-translate-y-1 hover:border-emerald-200 hover:shadow-[0_18px_50px_rgba(4,120,87,0.12)]" data-testid={`services-card-${c.href.replace(/\//g, "")}`}>
          <span className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 transition-colors group-hover:bg-emerald-700 group-hover:text-white"><c.icon size={22} /></span>
          <h3 className="font-display text-xl font-bold text-slate-900">{c.name}</h3>
          <p className="mt-2 flex-1 text-slate-600">{c.short}</p>
          <Link to={c.href} data-testid={`learn-more-${c.href.replace(/\//g, "")}`} className="mt-5 inline-flex items-center gap-1.5 font-bold text-emerald-700 hover:gap-2.5 transition-all">
            Learn More <ArrowUpRight size={16} />
          </Link>
        </div>
      </StaggerItem>
    ))}
  </Stagger>
);

export default function ServicesPage() {
  useLenis();
  useSEO({
    title: "Cleaning Services in Dublin | GoCleaning Dublin",
    description: "All our residential and commercial cleaning services in Dublin and Kildare — end of tenancy, move-out, deep, apartment, house, landlord, office, commercial and contract cleaning.",
    canonical: `${SITE.url}/services`,
    jsonLd: {
      "@context": "https://schema.org", "@type": "CleaningService", name: SITE.name,
      url: `${SITE.url}/services`, telephone: SITE.phoneHref, email: SITE.email,
      areaServed: ["Dublin", "County Kildare"],
    },
  });

  return (
    <div className="App bg-white">
      <Header />
      <main>
        <section className="bg-white pt-32 pb-8 sm:pt-36" data-testid="services-page-hero">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <p className="text-sm font-bold uppercase tracking-widest text-emerald-700">Our services</p>
              <h1 className="mt-3 font-display text-5xl font-black tracking-tighter text-slate-900 sm:text-6xl">Cleaning Services in Dublin</h1>
              <p className="mt-4 max-w-2xl text-lg text-slate-600">Residential and commercial cleaning across Dublin, County Kildare and surrounding areas. Choose a service to see what's included and get a fast quote.</p>
            </Reveal>
          </div>
        </section>

        <section className="bg-white py-12" data-testid="residential-services">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal><h2 id="residential" className="font-display text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">Residential Cleaning</h2></Reveal>
            <Grid cards={RESIDENTIAL_CARDS} />
          </div>
        </section>

        <section className="bg-slate-50 py-16" data-testid="commercial-services">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal><h2 id="commercial" className="font-display text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">Commercial Cleaning</h2></Reveal>
            <Grid cards={COMMERCIAL_CARDS} />
          </div>
        </section>
      </main>
      <Footer />
      <FloatingCTA />
    </div>
  );
}
